import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Phone, MessageCircle, MapPin, Share2, PawPrint, AlertTriangle,
  Eye, Stethoscope, Pill, HeartPulse, Info, Cpu,
  UserRound, ShieldAlert, ArrowRight, Navigation,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

// Format phone for WhatsApp: strip non-digits, replace leading 0 with UK 44.
const formatPhoneForWhatsApp = (phone) => {
  if (!phone) return '';
  let cleaned = phone.replace(/[^0-9]/g, '');
  if (cleaned.startsWith('0')) cleaned = '44' + cleaned.substring(1);
  return cleaned;
};

const getLocation = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('Geolocation not supported'));
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0,
    });
  });

/* -------------------------------------------------------------------------- */
/*  Reusable UI                                                               */
/* -------------------------------------------------------------------------- */

function DetailPill({ label, value }) {
  if (!value) return null;
  return (
    <div className="bg-muted/60 rounded-xl px-4 py-3">
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
        {label}
      </p>
      <p className="mt-1 text-sm font-medium text-foreground">{value}</p>
    </div>
  );
}

function MedicalRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-3 items-start" data-testid={`medical-${label.toLowerCase()}`}>
      <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
        <Icon className="w-4.5 h-4.5 text-primary" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
          {label}
        </p>
        <p className="mt-0.5 text-sm text-foreground leading-relaxed break-words">
          {value}
        </p>
      </div>
    </div>
  );
}

function ContactCard({ role, name, phone, primary = false, testId }) {
  if (!phone) return null;
  return (
    <a
      href={`tel:${phone}`}
      className={`flex items-center justify-between gap-3 rounded-2xl border transition-all ${
        primary
          ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90 shadow-lg shadow-primary/20 py-4 px-5'
          : 'bg-card text-foreground border-border hover:border-primary/40 py-3.5 px-4'
      }`}
      data-testid={testId}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
          primary ? 'bg-primary-foreground/15' : 'bg-primary/15'
        }`}>
          <Phone className={`w-5 h-5 ${primary ? 'text-primary-foreground' : 'text-primary'}`} />
        </div>
        <div className="min-w-0">
          <p className={`text-[10px] uppercase tracking-widest font-semibold ${primary ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
            {role}
          </p>
          <p className={`text-base font-heading font-semibold truncate ${primary ? '' : 'text-foreground'}`}>
            {name || 'Owner'}
          </p>
        </div>
      </div>
      <ArrowRight className={`w-5 h-5 shrink-0 ${primary ? 'text-primary-foreground/80' : 'text-primary'}`} />
    </a>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

export default function PetProfilePage() {
  const { petId } = useParams();
  const [pet, setPet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locationBusy, setLocationBusy] = useState(false);
  const [locationChooserOpen, setLocationChooserOpen] = useState(false);

  useEffect(() => {
    const fetchPet = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/pets/${petId}`);
        setPet(response.data);
        // SEO / social meta for the found-pet page
        document.title = `Help ${response.data.pet_name} get home · SaferPaws`;
      } catch (err) {
        setError(err.response?.data?.detail || 'Pet not found');
      } finally {
        setLoading(false);
      }
    };
    fetchPet();
  }, [petId]);

  /* ---- Actions ----------------------------------------------------------- */

  const handleCall = () => (window.location.href = `tel:${pet.owner_phone}`);

  const handleWhatsApp = () => {
    const message = encodeURIComponent(
      `Hi, I found ${pet.pet_name}! Please contact me so we can reunite you.`
    );
    const phone = formatPhoneForWhatsApp(pet.owner_phone);
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  const handleSMS = () => {
    const message = encodeURIComponent(
      `Hi, I found ${pet.pet_name}! Please contact me so we can reunite you.`
    );
    window.location.href = `sms:${pet.owner_phone}?body=${message}`;
  };

  const shareLocation = async (channel) => {
    setLocationBusy(true);
    try {
      const position = await getLocation();
      const { latitude, longitude } = position.coords;
      const mapsUrl = `https://maps.google.com/?q=${latitude},${longitude}`;
      const text = `Hi, I found ${pet.pet_name}!\n\nMy current location:\n${mapsUrl}`;
      if (channel === 'whatsapp') {
        const phone = formatPhoneForWhatsApp(pet.owner_phone);
        window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, '_blank');
      } else {
        window.location.href = `sms:${pet.owner_phone}?body=${encodeURIComponent(text)}`;
      }
    } catch (err) {
      if (err.code === 1) {
        alert('Please enable location permissions to share your location.');
      } else {
        alert('Could not get your location. Please try again or share manually.');
      }
    } finally {
      setLocationBusy(false);
      setLocationChooserOpen(false);
    }
  };

  const openDirections = () => {
    // Best-effort: opens Google Maps looking for finder's location + pet's postcode if provided,
    // otherwise opens a search for veterinary help near the finder.
    window.open('https://maps.google.com/?q=nearest+vet', '_blank');
  };

  const handleShare = async () => {
    const shareData = {
      title: pet.is_lost ? `Lost pet: ${pet.pet_name}` : `${pet.pet_name} · SaferPaws`,
      text: pet.is_lost
        ? `Please help find ${pet.pet_name} — share to help them get home.`
        : `Help ${pet.pet_name} get home safely.`,
      url: window.location.href,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        /* user cancelled */
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard.');
    }
  };

  /* ---- Loading / Error --------------------------------------------------- */

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <PawPrint className="w-12 h-12 text-primary mb-4" />
          <p className="text-muted-foreground">Loading pet profile…</p>
        </div>
      </div>
    );
  }

  if (error || !pet) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <PawPrint className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold text-foreground mb-2">Pet Not Found</h1>
          <p className="text-muted-foreground mb-6">
            {error || 'This tag may not be registered yet. If you found a pet, please contact a local vet.'}
          </p>
          <Link to="/" className="text-primary underline">Back to SaferPaws</Link>
        </div>
      </div>
    );
  }

  /* ---- Derived ----------------------------------------------------------- */

  const petPhoto = pet.photo_url ||
    'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=800&q=80&auto=format&fit=crop';

  const hasAnyMedical =
    pet.allergies || pet.medication || pet.medical_conditions ||
    pet.medical_notes || pet.vet_name || pet.vet_phone || pet.emergency_instructions;

  const hasAnyDetail = pet.breed || pet.age || pet.sex || pet.color || pet.temperament || pet.microchip_registered;

  /* ---- Render ------------------------------------------------------------ */

  return (
    <div className="min-h-screen bg-background text-foreground pb-24" data-testid="pet-profile-page">
      {/* ============================================================ */}
      {/* HERO                                                         */}
      {/* ============================================================ */}
      <section className="relative isolate overflow-hidden">
        {/* Blurred backdrop from the pet photo — provides warmth + colour */}
        <img
          src={petPhoto}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 -z-10 w-full h-full object-cover blur-3xl scale-125 opacity-30"
        />
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.92) 85%)',
          }}
          aria-hidden="true"
        />

        <div className="max-w-xl mx-auto px-4 pt-10 pb-8 sm:pt-14">
          {/* Lost banner */}
          {pet.is_lost && (
            <div
              className="mb-6 flex items-center gap-3 rounded-2xl border-2 border-destructive/70 bg-destructive/10 px-4 py-3 backdrop-blur-sm"
              data-testid="lost-banner"
              role="alert"
            >
              <AlertTriangle className="w-5 h-5 text-destructive shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-bold text-destructive uppercase tracking-wider">
                  Missing pet
                </p>
                <p className="text-xs text-foreground/90 mt-0.5">
                  Please contact the owner as soon as possible.
                </p>
              </div>
              {pet.reward_offered && (
                <span className="hidden sm:inline text-xs bg-primary text-primary-foreground font-bold px-3 py-1 rounded-full">
                  Reward: {pet.reward_offered}
                </span>
              )}
            </div>
          )}

          {/* Pet photo */}
          <div className="flex justify-center">
            <div
              className={`relative w-40 h-40 sm:w-48 sm:h-48 rounded-full overflow-hidden ring-4 ${
                pet.is_lost
                  ? 'ring-destructive shadow-[0_0_60px_rgba(239,68,68,0.35)]'
                  : 'ring-primary shadow-[0_0_50px_rgba(239,106,31,0.4)]'
              }`}
              data-testid="pet-photo"
            >
              <img src={petPhoto} alt={`${pet.pet_name}`} className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Greeting */}
          <div className="text-center mt-8">
            <p className="text-sm uppercase tracking-[0.3em] text-primary font-semibold">
              Hi, I&apos;m
            </p>
            <h1
              className="mt-2 text-5xl sm:text-6xl font-heading font-black tracking-tighter text-foreground"
              data-testid="pet-name"
            >
              {pet.pet_name} <span className="inline-block">🐾</span>
            </h1>
            <p className="mt-5 text-lg text-muted-foreground leading-relaxed max-w-md mx-auto">
              Thank you for stopping to help me.{' '}
              <br className="hidden sm:block" />
              My family will be so grateful.
            </p>
          </div>

          {/* Reward pill on mobile (dedicated line so it never wraps oddly) */}
          {pet.is_lost && pet.reward_offered && (
            <div className="sm:hidden mt-4 flex justify-center">
              <span className="text-sm bg-primary text-primary-foreground font-bold px-4 py-1.5 rounded-full">
                💰 Reward: {pet.reward_offered}
              </span>
            </div>
          )}
        </div>
      </section>

      <main className="max-w-xl mx-auto px-4 space-y-6">
        {/* ============================================================ */}
        {/* PRIMARY ACTIONS — the whole point of this page               */}
        {/* ============================================================ */}
        <section aria-labelledby="actions-heading" data-testid="primary-actions">
          <h2 id="actions-heading" className="sr-only">Contact the owner</h2>

          {/* Call — biggest, most visible */}
          <button
            onClick={handleCall}
            className="w-full flex items-center justify-center gap-3 py-6 rounded-2xl bg-primary text-primary-foreground text-xl font-bold shadow-2xl shadow-primary/30 hover:bg-primary/90 active:scale-[0.99] transition-all"
            data-testid="call-owner-btn"
          >
            <Phone className="w-6 h-6" />
            Call {pet.owner_name?.split(' ')[0] || 'Owner'}
          </button>

          {/* Share Location */}
          {!locationChooserOpen ? (
            <button
              onClick={() => setLocationChooserOpen(true)}
              className="mt-3 w-full flex items-center justify-center gap-3 py-5 rounded-2xl bg-card border border-border text-foreground text-lg font-semibold hover:border-primary/60 active:scale-[0.99] transition-all"
              data-testid="share-location-btn"
            >
              <MapPin className="w-5 h-5 text-primary" />
              Share My Location
            </button>
          ) : (
            <div className="mt-3 rounded-2xl bg-card border border-primary/40 p-4">
              <p className="text-sm font-semibold text-foreground mb-3 text-center">
                Send my location via:
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => shareLocation('whatsapp')}
                  disabled={locationBusy}
                  className="flex items-center justify-center gap-2 py-4 rounded-xl bg-primary/10 border border-primary/40 text-primary font-semibold hover:bg-primary/20"
                  data-testid="location-whatsapp-btn"
                >
                  <MessageCircle className="w-5 h-5" />
                  {locationBusy ? '…' : 'WhatsApp'}
                </button>
                <button
                  onClick={() => shareLocation('sms')}
                  disabled={locationBusy}
                  className="flex items-center justify-center gap-2 py-4 rounded-xl bg-primary/10 border border-primary/40 text-primary font-semibold hover:bg-primary/20"
                  data-testid="location-sms-btn"
                >
                  <MessageCircle className="w-5 h-5" />
                  {locationBusy ? '…' : 'SMS'}
                </button>
              </div>
              <button
                onClick={() => setLocationChooserOpen(false)}
                className="w-full mt-3 text-xs text-muted-foreground hover:text-foreground py-1"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Send a Message */}
          <button
            onClick={handleWhatsApp}
            className="mt-3 w-full flex items-center justify-center gap-3 py-5 rounded-2xl bg-card border border-border text-foreground text-lg font-semibold hover:border-primary/60 active:scale-[0.99] transition-all"
            data-testid="whatsapp-owner-btn"
          >
            <MessageCircle className="w-5 h-5 text-primary" />
            Send a Message
          </button>

          {/* Get Directions (nearest vet — helpful if pet is injured) */}
          <button
            onClick={openDirections}
            className="mt-3 w-full flex items-center justify-center gap-3 py-5 rounded-2xl bg-card border border-border text-foreground text-lg font-semibold hover:border-primary/60 active:scale-[0.99] transition-all"
            data-testid="directions-btn"
          >
            <Navigation className="w-5 h-5 text-primary" />
            Directions to Nearest Vet
          </button>

          {/* SMS quiet fallback */}
          <button
            onClick={handleSMS}
            className="mt-3 w-full text-sm text-muted-foreground hover:text-primary py-2"
            data-testid="sms-owner-btn"
          >
            Or send a text message instead →
          </button>
        </section>

        {/* ============================================================ */}
        {/* MEDICAL — only shown if any medical info exists              */}
        {/* ============================================================ */}
        {hasAnyMedical && (
          <section
            className="rounded-2xl border border-primary/40 bg-primary/[0.06] p-5 sm:p-6"
            data-testid="medical-section"
            aria-labelledby="medical-heading"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                <Stethoscope className="w-4 h-4 text-primary" strokeWidth={2.25} />
              </div>
              <h2
                id="medical-heading"
                className="text-sm uppercase tracking-widest font-bold text-primary"
              >
                Important Medical Info
              </h2>
            </div>

            <div className="space-y-4">
              <MedicalRow icon={ShieldAlert}   label="Allergies"          value={pet.allergies} />
              <MedicalRow icon={Pill}          label="Medication"         value={pet.medication} />
              <MedicalRow icon={HeartPulse}    label="Medical Conditions" value={pet.medical_conditions} />
              <MedicalRow icon={Info}          label="Medical Notes"      value={pet.medical_notes} />
              <MedicalRow icon={AlertTriangle} label="Emergency Instructions" value={pet.emergency_instructions} />
            </div>

            {(pet.vet_name || pet.vet_phone) && (
              <div className="mt-5 pt-5 border-t border-primary/20">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
                  Registered Vet
                </p>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <p className="text-sm text-foreground font-medium">{pet.vet_name || 'On file'}</p>
                  {pet.vet_phone && (
                    <a
                      href={`tel:${pet.vet_phone}`}
                      className="inline-flex items-center gap-1.5 text-sm text-primary font-semibold hover:underline"
                      data-testid="call-vet-btn"
                    >
                      <Phone className="w-4 h-4" />
                      Call Vet
                    </a>
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        {/* ============================================================ */}
        {/* OWNER PERSONAL MESSAGE                                        */}
        {/* ============================================================ */}
        {(pet.owner_message || pet.behaviour_notes) && (
          <section
            className="rounded-2xl bg-card border border-border p-5 sm:p-6"
            data-testid="owner-message-section"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                <UserRound className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
                  A message from {pet.owner_name?.split(' ')[0] || 'the family'}
                </p>
                <p className="mt-2 text-base text-foreground leading-relaxed italic">
                  &ldquo;{pet.owner_message || pet.behaviour_notes}&rdquo;
                </p>
              </div>
            </div>
          </section>
        )}

        {/* ============================================================ */}
        {/* PET DETAILS                                                  */}
        {/* ============================================================ */}
        {hasAnyDetail && (
          <section
            className="rounded-2xl bg-card border border-border p-5 sm:p-6"
            data-testid="pet-details-section"
            aria-labelledby="details-heading"
          >
            <h2
              id="details-heading"
              className="text-sm uppercase tracking-widest font-bold text-primary mb-4"
            >
              About {pet.pet_name}
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <DetailPill label="Breed"       value={pet.breed} />
              <DetailPill label="Age"         value={pet.age} />
              <DetailPill label="Sex"         value={pet.sex} />
              <DetailPill label="Colour"      value={pet.color} />
              <DetailPill label="Temperament" value={pet.temperament} />
              {pet.microchip_registered && (
                <div className="bg-muted/60 rounded-xl px-4 py-3">
                  <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5">
                    <Cpu className="w-3 h-3" />
                    Microchip
                  </p>
                  <p className="mt-1 text-sm font-medium text-foreground">
                    {pet.microchip_number || 'Registered'}
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ============================================================ */}
        {/* MULTIPLE CONTACTS                                            */}
        {/* ============================================================ */}
        <section
          className="rounded-2xl bg-card border border-border p-5 sm:p-6"
          data-testid="contacts-section"
          aria-labelledby="contacts-heading"
        >
          <h2
            id="contacts-heading"
            className="text-sm uppercase tracking-widest font-bold text-primary mb-4"
          >
            Reach the family
          </h2>
          <div className="space-y-3">
            <ContactCard
              role="Primary owner"
              name={pet.owner_name}
              phone={pet.owner_phone}
              primary
              testId="contact-primary"
            />
            <ContactCard
              role="Secondary owner"
              name={pet.secondary_contact_name || (pet.backup_phone ? 'Family member' : null)}
              phone={pet.secondary_contact_phone || pet.backup_phone}
              testId="contact-secondary"
            />
            <ContactCard
              role="Emergency contact"
              name={pet.emergency_contact_name}
              phone={pet.emergency_contact_phone}
              testId="contact-emergency"
            />
          </div>
        </section>

        {/* ============================================================ */}
        {/* SIGHTING / SHARE                                              */}
        {/* ============================================================ */}
        <section className="space-y-3">
          {pet.is_lost && (
            <Link to={`/report-sighting/${petId}`} className="block">
              <button
                className="w-full flex items-center justify-center gap-3 py-5 rounded-2xl bg-card border border-destructive/50 text-destructive text-base font-semibold hover:bg-destructive/5 active:scale-[0.99] transition-all"
                data-testid="report-sighting-btn"
              >
                <Eye className="w-5 h-5" />
                Report a Sighting
              </button>
            </Link>
          )}
          <button
            onClick={handleShare}
            className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl bg-muted/60 hover:bg-muted text-muted-foreground text-sm font-medium"
            data-testid="share-pet-btn"
          >
            <Share2 className="w-4 h-4" />
            Share {pet.pet_name}&apos;s profile
          </button>
        </section>

        {/* ============================================================ */}
        {/* TRUST FOOTER                                                 */}
        {/* ============================================================ */}
        <footer
          className="text-center pt-8 pb-4"
          data-testid="saferpaws-footer"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
          >
            <PawPrint className="w-4 h-4" />
            <span className="text-xs">
              This pet is protected by <span className="font-bold">
                <span className="text-foreground">Safer</span><span className="text-primary">Paws</span>
              </span>
            </span>
          </Link>
          <p className="mt-1 text-[11px] text-muted-foreground/80">
            Helping reunite lost pets with their families.
          </p>
        </footer>
      </main>
    </div>
  );
}
